import { Link } from 'react-router-dom'

export interface MetricBar {
  label: string
  value: number
  href?: string
  colorClass: string
}

interface LeadCounts {
  new_business: number
  esign: number
  verification: number
  underwriting: number
  build: number
  live: number
}

interface PortfolioCounts {
  active_merchants: number
  open_chargebacks: number
  no_batch_7_days: number
  first_batches_this_week: number
}

export function pipelineBars(counts: LeadCounts | undefined): MetricBar[] | null {
  if (!counts) return null

  return [
    { label: 'New', value: counts.new_business, href: '/leads?stage=new_business', colorClass: 'bg-slate-400' },
    // eSign, underwriting, and build each add two stages. The lead list
    // filters on one stage, so those bars are not links.
    { label: 'eSign', value: counts.esign, colorClass: 'bg-brand-500' },
    { label: 'Verification', value: counts.verification, href: '/leads?stage=verification', colorClass: 'bg-emerald-500' },
    { label: 'Underwriting', value: counts.underwriting, colorClass: 'bg-amber-500' },
    { label: 'Build', value: counts.build, colorClass: 'bg-teal-500' },
    { label: 'Live', value: counts.live, href: '/leads?stage=live', colorClass: 'bg-emerald-700' },
  ]
}

export function portfolioBars(portfolio: PortfolioCounts | undefined): MetricBar[] | null {
  if (!portfolio) return null

  return [
    { label: 'Active merchants', value: portfolio.active_merchants, href: '/merchants?status=active', colorClass: 'bg-brand-600' },
    { label: 'Open chargebacks', value: portfolio.open_chargebacks, href: '/merchants?has_open_chargebacks=true', colorClass: 'bg-rose-500' },
    { label: 'No batch 7 days', value: portfolio.no_batch_7_days, href: '/merchants?no_batch_7_days=true', colorClass: 'bg-amber-500' },
    { label: 'First batches this week', value: portfolio.first_batches_this_week, href: '/merchants?first_batch_this_week=true', colorClass: 'bg-teal-600' },
  ]
}

export function MetricBars({
  items,
  label,
  status = 'ready',
  onRetry,
}: {
  items: MetricBar[] | null
  label: string
  status?: 'loading' | 'error' | 'ready'
  onRetry?: () => void
}) {
  if (status === 'error') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-rose-700">Could not load these counts.</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink hover:bg-slate-50"
          >
            Retry
          </button>
        ) : null}
      </div>
    )
  }

  if (!items || status === 'loading') {
    return <p className="text-sm text-muted">Loading</p>
  }

  const max = Math.max(...items.map((item) => item.value), 0)

  return (
    <ul className="space-y-3" aria-label={label}>
      {items.map((item) => {
        const width = max === 0 ? 0 : Math.max(4, Math.round((item.value / max) * 100))
        const shown = item.value === 0 ? 0 : width
        const body = (
          <>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="font-medium text-slate-700">{item.label}</span>
              <span className="tabular-nums font-semibold text-ink">{item.value.toLocaleString()}</span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className={`h-2 rounded-full ${item.colorClass}`}
                style={{ width: `${shown}%` }}
              />
            </div>
          </>
        )

        return (
          <li key={item.label}>
            {item.href ? (
              <Link to={item.href} className="block rounded-lg px-1 py-1 hover:bg-brand-50">
                {body}
              </Link>
            ) : body}
          </li>
        )
      })}
    </ul>
  )
}
